// ArenaEnvironment — a stylised 3D biome dressing that reads well in a
// mobile browser. Uses procedural geometry only (no external assets) so it
// works offline and loads in a single frame.
//
// iter35 upgrade — every scenery mesh here is tagged with
//   userData.occludable = true            (opt-in target for CameraOcclusion)
//   userData.small = true|false           (small foliage fades to 0; large trees to 0.28)
// and their materials start `transparent: true` so the camera can smoothly
// dial down opacity as it detects them crossing the camera-to-creature ray.
import { useMemo } from 'react';
import * as THREE from 'three';
import { Instances, Instance } from '@react-three/drei';

export default function ArenaEnvironment({ arena, weather, lowPower }) {
  const palette = PALETTE[arena] || PALETTE.forest;

  return (
    <>
      {/* Ground */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]}>
        <planeGeometry args={[80, 80, 32, 32]} />
        <meshStandardMaterial color={palette.ground} roughness={0.94} metalness={0.02} />
      </mesh>

      {/* Wet arenas get a reflective water strip */}
      {['wetlands', 'coast', 'river', 'lake', 'swamp'].includes(arena) && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, -14]}>
          <planeGeometry args={[80, 32]} />
          <meshStandardMaterial color={palette.water || '#22354a'} roughness={0.15} metalness={0.55} emissive={palette.water || '#22354a'} emissiveIntensity={0.05} />
        </mesh>
      )}

      {/* Snow overlay */}
      {(arena === 'snow' || arena === 'tundra' || weather === 'snow') && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <planeGeometry args={[80, 80]} />
          <meshStandardMaterial color="#e4ecf3" roughness={0.98} metalness={0.02} transparent opacity={0.42} />
        </mesh>
      )}

      {/* Distant hills — deliberately NOT occludable; they define the horizon. */}
      <HillsRing color={palette.hill} count={lowPower ? 10 : 18} />

      {/* Vegetation — trees for temperate biomes, cacti for desert, boulders for mountain */}
      {arena === 'desert' ? (
        <ScatterCacti count={lowPower ? 12 : 22} color={palette.tree} />
      ) : ['mountain', 'cave', 'tundra', 'snow'].includes(arena) ? (
        <ScatterBoulders count={lowPower ? 10 : 20} color={palette.tree} />
      ) : (
        <ScatterTrees count={lowPower ? 20 : 40} color={palette.tree} accent={palette.leaf} />
      )}

      {/* Ambient sky dome — NOT occludable; it sits behind everything. */}
      <mesh position={[0, 20, 0]}>
        <sphereGeometry args={[80, 32, 16]} />
        <meshBasicMaterial color={palette.sky} side={THREE.BackSide} transparent opacity={0.7} />
      </mesh>
    </>
  );
}


const PALETTE = {
  forest:   { ground: '#243b23', hill: '#152918', tree: '#2f5730', leaf: '#4b8f4d', sky: '#254633', water: '#2a4a48' },
  wetlands: { ground: '#254536', hill: '#152a24', tree: '#3a6b4e', leaf: '#6ea774', sky: '#2c4b4a', water: '#25455a' },
  coast:    { ground: '#c4b58d', hill: '#847156', tree: '#4a6b3d', leaf: '#7fa14a', sky: '#3f5e75', water: '#264866' },
  mountain: { ground: '#4c4c58', hill: '#26262e', tree: '#7a8391', leaf: '#a0a8b2', sky: '#4a4c60' },
  meadow:   { ground: '#3e6c25', hill: '#2a4e19', tree: '#54782d', leaf: '#a4c14a', sky: '#3f5e75' },
  desert:   { ground: '#a67640', hill: '#6b4926', tree: '#617f52', leaf: '#a7c67c', sky: '#8a5f3a' },
  cave:     { ground: '#1a1922', hill: '#101018', tree: '#3a3644', leaf: '#5a5464', sky: '#0d0d16' },
  tundra:   { ground: '#7994a7', hill: '#4b5b6c', tree: '#4c5b64', leaf: '#8fa5b3', sky: '#5d7386' },
  snow:     { ground: '#dee9ef', hill: '#a3bccb', tree: '#546b78', leaf: '#8a9ba5', sky: '#7e93a3' },
  river:    { ground: '#3d4b32', hill: '#2c3624', tree: '#4a6b36', leaf: '#8ab148', sky: '#3f5e75', water: '#2c526a' },
  lake:     { ground: '#334033', hill: '#1e2b1e', tree: '#3f5e2f', leaf: '#87b04c', sky: '#3f5e75', water: '#264a5a' },
  swamp:    { ground: '#2a3626', hill: '#182018', tree: '#3a5b3a', leaf: '#7fa055', sky: '#2e3d38', water: '#2c3e3a' },
};


function HillsRing({ color, count }) {
  const positions = useMemo(() =>
    Array.from({ length: count }, (_, i) => {
      const a = (i / count) * Math.PI * 2;
      const r = 34 + (i * 3.13) % 6;
      return {
        pos: [Math.cos(a) * r, 0.2 + (i * 7 % 5) * 0.6, Math.sin(a) * r],
        s: 3 + (i * 1.7) % 4,
      };
    }), [count]);
  return (
    <>
      {positions.map((p, i) => (
        <mesh key={i} position={p.pos}>
          <coneGeometry args={[p.s, p.s * 1.5, 12]} />
          <meshStandardMaterial color={color} roughness={1} metalness={0} />
        </mesh>
      ))}
    </>
  );
}


function ScatterTrees({ count, color, accent }) {
  const positions = useMemo(() =>
    Array.from({ length: count }, (_, i) => {
      const a = (i / count) * Math.PI * 2 + (i * 0.13);
      const r = 8 + (i * 2.3) % 18;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      // Push away from the combat lane (-4..+4 on x)
      if (Math.abs(x) < 5.5 && Math.abs(z) < 4.5) return null;
      return { pos: [x, 0, z], s: 0.85 + (i * 0.31) % 0.55 };
    }).filter(Boolean), [count]);
  return (
    <>
      {positions.map((p, i) => (
        <group key={i} position={p.pos} scale={p.s}>
          <mesh castShadow position={[0, 1.1, 0]} userData={{ occludable: true, small: false }}>
            <cylinderGeometry args={[0.13, 0.18, 2.1, 6]} />
            <meshStandardMaterial color={color} roughness={0.9} transparent opacity={1} />
          </mesh>
          <mesh castShadow position={[0, 2.6, 0]} userData={{ occludable: true, small: false }}>
            <coneGeometry args={[1.05, 2.5, 8]} />
            <meshStandardMaterial color={accent} roughness={0.85} transparent opacity={1} />
          </mesh>
        </group>
      ))}
    </>
  );
}


function ScatterCacti({ count, color }) {
  const positions = useMemo(() =>
    Array.from({ length: count }, (_, i) => {
      const a = (i / count) * Math.PI * 2 + (i * 0.19);
      const r = 8 + (i * 1.9) % 16;
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (Math.abs(x) < 5.5 && Math.abs(z) < 4.5) return null;
      return { pos: [x, 0, z], h: 1.1 + (i * 0.19) % 0.9 };
    }).filter(Boolean), [count]);
  return positions.map((p, i) => (
    <mesh key={i} position={[p.pos[0], p.h * 0.5, p.pos[2]]} userData={{ occludable: true, small: false }}>
      <cylinderGeometry args={[0.35, 0.42, p.h, 8]} />
      <meshStandardMaterial color={color} roughness={0.85} transparent opacity={1} />
    </mesh>
  ));
}


function ScatterBoulders({ count, color }) {
  const positions = useMemo(() =>
    Array.from({ length: count }, (_, i) => {
      const a = (i / count) * Math.PI * 2 + (i * 0.17);
      const r = 7 + (i * 2.1) % 20;
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (Math.abs(x) < 5.5 && Math.abs(z) < 4.5) return null;
      return { pos: [x, 0.3, z], s: 0.6 + (i * 0.27) % 1.1 };
    }).filter(Boolean), [count]);
  return positions.map((p, i) => (
    <mesh key={i} position={p.pos} rotation={[i, i * 0.7, i * 1.3]} userData={{ occludable: true, small: false }}>
      <dodecahedronGeometry args={[p.s]} />
      <meshStandardMaterial color={color} roughness={0.95} metalness={0.05} transparent opacity={1} />
    </mesh>
  ));
}
